import { useLocalization } from "@fluent/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@lending/components/Card/Card";
import { formatCentsAsDollars } from "@lending/utils/formatters";
import styles from "./Slider.module.css";

interface SliderProps {
  min?: number;
  max: number;
  value: number;
  onChange: (val: number) => void;
  onValidityChange?: (isValid: boolean) => void;
  step?: number;
  editable?: boolean;
}

function getSliderPercentage(value: number, min: number, max: number) {
  const denominator = max - min;

  if (denominator <= 0) {
    return 1;
  }

  return Math.min(Math.max((value - min) / denominator, 0), 1);
}

const CURRENCY_INPUT_PATTERN = /^\d*(\.\d{0,2})?$/;

function getCentsFromInputValue(value: string) {
  if (!CURRENCY_INPUT_PATTERN.test(value)) {
    return null;
  }

  const [dollars = "", cents = ""] = value.split(".");

  if (!dollars && !cents) {
    return null;
  }

  return Number(dollars || "0") * 100 + Number(cents.padEnd(2, "0"));
}

function getInputMax(min: number, max: number, step: number) {
  const denominator = max - min;

  if (step <= 0 || denominator <= 0) {
    return max;
  }

  // A range input snaps to `min + k*step`, so an unaligned max is unreachable by
  // dragging. Extend the attribute to the next step and clamp back in onChange.
  return min + Math.ceil(denominator / step) * step;
}

function Slider({ min, max, value, onChange, onValidityChange, step }: SliderProps) {
  const { l10n } = useLocalization();
  const containerRef = useRef<HTMLDivElement>(null);
  const amountRectangleRef = useRef<HTMLElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);
  const amountInputSizerRef = useRef<HTMLElement>(null);

  const [amountRectPos, setAmountRectPos] = useState(0);

  const [inputAmount, setInputAmount] = useState(`${value / 100}`);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputError, setInputError] = useState(false);

  const parsedInputAmountValue = getCentsFromInputValue(inputAmount);

  function onSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputVisible(false);
    const sliderValue = Math.min(Number(e.target.value), max);
    setInputAmount(`${sliderValue / 100}`);
    updateSliderPosition(sliderValue);
    onChange(sliderValue);
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const nextValue = e.target.value.replace(/[^0-9.]/g, "");

    if (CURRENCY_INPUT_PATTERN.test(nextValue)) {
      setInputAmount(nextValue);
    }
  }

  function onInputBlur(e: React.FocusEvent<HTMLInputElement>) {
    const nextInputAmount = e.currentTarget.value;
    const nextValue = getCentsFromInputValue(nextInputAmount);

    if (nextValue == null) {
      setInputAmount(nextInputAmount);
      setInputError(true);
      onValidityChange?.(false);
      return;
    }

    setInputVisible(false);
    onChange(nextValue);
    updateSliderPosition(nextValue);
  }

  const updateSliderPosition = useCallback(
    (val: number) => {
      let amountPos = 0;
      let containerWidth = 0;
      if (containerRef.current) {
        const amountPercentage = getSliderPercentage(val, min ?? 0, max);
        containerWidth = Number(containerRef.current.offsetWidth);
        amountPos = containerWidth * amountPercentage;
      }
      if (amountRectangleRef.current) {
        const amountRectWidth = Number(amountRectangleRef.current.offsetWidth);
        if (amountPos - amountRectWidth / 2 > 0) {
          amountPos = amountPos - amountRectWidth / 2;
        } else {
          amountPos = 0;
        }
        const rightSideOffset = containerWidth - (amountPos + amountRectWidth);
        if (rightSideOffset < 0) {
          amountPos = amountPos + rightSideOffset;
        }
      }
      setAmountRectPos(amountPos);
    },
    [max, min]
  );

  useEffect(() => {
    const hasInvalidInputAmount = parsedInputAmountValue == null;
    const valueForValidation = inputVisible ? (parsedInputAmountValue ?? value) : value;

    setInputError(
      valueForValidation > max || valueForValidation < (min ?? 0) || hasInvalidInputAmount
    );
    onValidityChange?.(!hasInvalidInputAmount);
    if (amountInputRef.current && amountInputSizerRef.current) {
      amountInputRef.current.style.width = `${amountInputSizerRef.current.offsetWidth + 5}px`;
    }
    updateSliderPosition(value);
  }, [
    inputVisible,
    max,
    min,
    onValidityChange,
    parsedInputAmountValue,
    value,
    updateSliderPosition,
  ]);

  return (
    <Card>
      <div className={`${styles.amountLabelContainer} margin--bottom--m`}>
        <div
          className={styles.amountLabelPositioner}
          style={{ transform: `translateX(${amountRectPos}px)` }}
        >
          <span
            className={`${styles.amountContainer} ${inputVisible && styles.focus} ${inputError && styles.error}`}
            ref={amountRectangleRef}
          >
            {inputVisible ? (
              <>
                $
                <input
                  autoFocus
                  type="decimal"
                  ref={amountInputRef}
                  className={styles.amountInput}
                  value={inputAmount}
                  onChange={onInputChange}
                  onBlur={onInputBlur}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const nextValue = parsedInputAmountValue;

                      if (nextValue == null) {
                        setInputError(true);
                        onValidityChange?.(false);
                        return;
                      }

                      setInputVisible(false);
                      onChange(nextValue);
                      updateSliderPosition(nextValue);
                    }
                  }}
                />
                <span
                  style={{ position: "absolute", visibility: "hidden" }}
                  ref={amountInputSizerRef}
                >
                  {inputAmount}
                </span>
              </>
            ) : (
              <div
                className={styles.amountNumber}
                onClick={() => {
                  setInputVisible(true);
                }}
              >
                {formatCentsAsDollars(value)}
              </div>
            )}
          </span>
        </div>
      </div>
      <div ref={containerRef} className={styles.sliderContainer}>
        <div className={styles.rangeTrackBackground} />
        <div
          className={styles.rangeTrackFill}
          style={{
            width: `${getSliderPercentage(value, min ?? 0, max) * 100}%`,
          }}
        />
        <input
          type="range"
          min={min ?? 0}
          max={getInputMax(min ?? 0, max, step ?? 1)}
          step={step ?? 1}
          value={value}
          onChange={onSliderChange}
          className={styles.rangeSlider}
        />
      </div>
      <div className="margin--top--xs" style={{ display: "flex" }}>
        {formatCentsAsDollars(min ?? 0)}
        <div style={{ flexGrow: 1 }} />
        {formatCentsAsDollars(max)}
      </div>
      {inputError && (
        <div className="margin--top--m">
          <span className="fontColor--error">
            {l10n.getString(
              "ui-slider-amount-out-of-range",
              {
                min: formatCentsAsDollars(min ?? 0),
                max: formatCentsAsDollars(max),
              },
              `Amount must be between ${formatCentsAsDollars(min ?? 0)} and ${formatCentsAsDollars(max)}.`
            )}
          </span>
        </div>
      )}
    </Card>
  );
}

export { Slider };
